import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { getHistoryChangedFields } from 'src/app/_helpers/history-changed-fields.helper';
import { buildHistoryParams, getHistoryDateTimeDisplay, loadPopupHistory } from 'src/app/_helpers/history-popup.helper';
import { getVersionEffectivePeriodDisplay, isHistoricalHistoryRow } from 'src/app/_helpers/oel-version.helper';
import { UnitSymbols } from 'src/app/enums/units.enum';
import { AgentJurisdictionRecordHistory } from 'src/app/models/agent-jurisdiction.model';
import { AgentJurisdictionService } from 'src/app/services/agent-jurisdiction.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';

/**
 * Read-only Version History for a single OEL (AgentJurisdiction) row.
 * Reuses the existing agent-details-popup history wiring
 * (buildHistoryParams / loadPopupHistory / getHistoryDateTimeDisplay /
 * getHistoryChangedFields) rather than HistoryGridComponent, per DASH-2618 AC.
 * All display strings are pre-computed in mapHistoryRecord — no method calls in the template.
 */
@Component({
    standalone: true,
    selector: 'app-agent-jurisdiction-history-dialog',
    imports: [CommonModule, MatDialogModule, MatTableModule, TranslateModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './agent-jurisdiction-history-dialog.component.html'
})
export class AgentJurisdictionHistoryDialogComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['status', 'oel', 'effectiveStart', 'effectiveEnd', 'source', 'updatedBy', 'updatedDate'];
  dataSource = new MatTableDataSource<AgentJurisdictionRecordHistory>();
  isLoading = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AgentJurisdictionHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: string,
    private agentJurisdictionService: AgentJurisdictionService,
    private dateFormatService: DateFormatService,
    private translateService: TranslateService,
    private valueConversionService: ValueConversionService,
    private toastService: ToastService,
  ) { }

  ngOnInit(): void {
    if (!this.data) {
      return;
    }
    this.isLoading = true;
    const params = buildHistoryParams('agentJurisdictionId', this.data);
    loadPopupHistory(
      this.agentJurisdictionService.getHistory(params),
      this.destroy$,
      (history) => this.mapHistoryRecord(history),
      (records) => {
        this.dataSource.data = records;
        this.isLoading = false;
      },
      (error) => {
        this.toastService.showError(error);
        this.dataSource.data = [];
        this.isLoading = false;
      },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.dialogRef.close();
  }

  private mapHistoryRecord(history: AgentJurisdictionRecordHistory): AgentJurisdictionRecordHistory {
    // DASH-2681: label from the backend isCurrent flag (one row per version, exactly one Current),
    // not the snapshotted effectiveEnd (every insert-snapshot is open → falsely "Current").
    const historical = isHistoricalHistoryRow(history);
    const hasEnd = history.effectiveEnd != null && history.effectiveEnd !== '';
    const hasStart = history.effectiveStart != null && history.effectiveStart !== '';
    return {
      ...history,
      changedFieldSet: new Set(getHistoryChangedFields(history)),
      isHistorical: historical,
      statusLabel: historical ? 'Historical' : 'Current',
      statusClass: historical ? 'status-inactive' : 'status-active',
      effectivePeriodDisplay: getVersionEffectivePeriodDisplay(history, this.dateFormatService, this.translateService),
      // Column-specific pre-computed displays (no method calls in template).
      oelValue: this.getOelValue(history),
      effectiveStartDisplay: hasStart
        ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(history.effectiveStart, this.dateFormatService.userDateFormat)
        : '-',
      // Effective End must follow the same isCurrent-derived flag as Status: a Historical row
      // whose insert-snapshot has effectiveEnd = null must NOT render "Current" here (DASH-2681).
      effectiveEndDisplay: !historical
        ? this.translateService.getDynamicTranslation('Current')
        : (hasEnd
          ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(history.effectiveEnd, this.dateFormatService.userDateFormat)
          : '-'),
      dateUpdatedDisplay: getHistoryDateTimeDisplay(history.dateUpdated, this.dateFormatService),
    } as AgentJurisdictionRecordHistory;
  }

  private getOelValue(row: AgentJurisdictionRecordHistory): string {
    if (row.oel == null) {
      return '-';
    }
    const agentUnit = row.agentUnits;
    const hasUnit = agentUnit != null;
    const val = hasUnit
      ? this.valueConversionService.getParameterValueAsPerAgentUnit(row.oel, agentUnit)
      : row.oel;
    return `${+val.toFixed(4)}${hasUnit ? ' ' + UnitSymbols[agentUnit] : ''}`;
  }
}
